import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StarRating from '@/components/base/StarRating';
import { categories, comics, siteInfo } from '@/mocks/data';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const navigate = useNavigate();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredComics = activeTab === 'all'
    ? comics
    : comics.filter((c) => c.category === activeTab);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `漫画分类 - ${siteInfo.name}`,
    description: `浏览${siteInfo.name}上所有分类的漫画，包括热血、奇幻、动作、搞笑等多种类型`,
    url: `${siteInfo.url}/category`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-8">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '漫画分类' }]} />
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">漫画分类</h1>
          <p className="text-sm text-foreground-500 mb-6">按类型筛选，找到你喜欢的漫画</p>

          <div className="flex flex-wrap gap-2 mb-8" role="tablist">
            {categories.map((cat) => (
              <button
                key={cat.id}
                role="tab"
                aria-selected={activeTab === cat.id}
                onClick={() => handleTabChange(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeTab === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} w-3.5 h-3.5 flex items-center justify-center`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filteredComics.map((comic) => (
              <article
                key={comic.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/article/${comic.id}`)}
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 古古漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
                <h3 className="font-heading text-sm md:text-base text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors truncate">
                  {comic.title}
                </h3>
                <StarRating rating={comic.rating} />
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-foreground-500 mt-1.5">{comic.views} 阅读 · {comic.chapters}话</p>
              </article>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
              <p className="text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}